import React, { useEffect, useState } from "react";
import videojs from "video.js";
import { useSelector } from "react-redux";

/**
 * Where the Equal Play plugin should render the transcript.
 *
 * It must live OUTSIDE the `.relative` wrapper below: the <video> element is
 * absolutely positioned against that wrapper on all four edges, so anything
 * added inside it makes the wrapper taller and stretches the player with it.
 * That is the "transcript makes the player grow" bug.
 */
const TRANSCRIPT_CONTAINER_ID = "eqp-transcript-container";

/**
 * Point the plugin at our own container instead of letting it guess.
 *
 * `transcriptContainer` landed in plugin v1.1.38; older players simply ignore
 * the option and keep their previous (broken) placement, so this is safe to
 * ship before every client's player has been updated.
 */
const setTranscriptContainer = (videoPlayer) => {
  try {
    // Only touch an already-initialised plugin — calling player.equalplay()
    // before the player config has initialised it would construct an instance
    // without the player's own configured options.
    const active =
      videoPlayer.activePlugins_ && videoPlayer.activePlugins_.equalplay;
    if (!active || typeof videoPlayer.equalplay !== "function") {
      console.warn(
        "[EQP] equalplay plugin not reachable on this player — transcriptContainer not set. " +
          "The transcript will fall back to the plugin's own placement.",
        { activePlugins: videoPlayer.activePlugins_ }
      );
      return false;
    }

    const eqp = videoPlayer.equalplay();
    if (!eqp || !eqp.options) {
      console.warn(
        "[EQP] equalplay instance has no options object — transcriptContainer not set.",
        eqp
      );
      return false;
    }

    eqp.options.transcriptContainer = `#${TRANSCRIPT_CONTAINER_ID}`;
    return true;
  } catch (err) {
    // Never let a plugin-shape change break the editor's player.
    console.warn("Could not set Equal Play transcriptContainer", err?.message);
    return false;
  }
};

export default function VideoJS({
  videoId,
  videoDuration,
  play,
  handleTracks,
  currentTime,
  player,
}) {
  const [videoCanPlay, setVideoCanPlay] = useState(false);

  const account_id = useSelector(
    (state) => state.user.selectedAccount.account_id
  );
  const player_id = useSelector((state) => state.user.player_id);

  let videoPlayer;

  useEffect(() => {
    if (videoCanPlay) {
      // eslint-disable-next-line
      videoPlayer = videojs("bcPlayer");
      //    console.log(videoPlayer);
      setTranscriptContainer(videoPlayer);
      player(videoPlayer);
      videoPlayer.on("timeupdate", function () {
        let s = this.currentTime();
        currentTime(s);
        //t.scrollToTime(s)
      });

      //    handleTracks(videoPlayer.mediainfo.text_tracks);
      videoDuration(Math.floor(videoPlayer.mediainfo.duration));

      return () => {
        videoPlayer = null;
      };
    }
  }, [videoCanPlay]);

  useEffect(() => {
    let script = document.createElement("script");

    script.src = `https://players.brightcove.net/${account_id}/${player_id}_default/index.min.js`;
    script.async = true;
    script.defer = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [player_id, account_id]);

  return (
    <div className="block max-w-2xl">
      {/* `relative` belongs on the aspect-ratio box, NOT on an outer wrapper.
          The player below is pinned to all four edges of its nearest positioned
          ancestor, so if that ancestor is an outer wrapper, anything appended
          into the wrapper (the plugin's transcript) makes it taller and drags
          the player's bottom edge down with it. Anchoring to the pt-16x9 box
          fixes the player's height at the 16:9 ratio no matter what follows. */}
      <div className="pt-16x9 relative">
        <video
          id="bcPlayer"
          data-video-id={videoId}
          data-account={account_id}
          data-player={player_id}
          data-embed="default"
          data-application-id="Equal Play"
          data-show-draft="true"
          className="video-js absolute top-0 right-0 left-0 bottom-0"
          onLoadedMetadata={(e) => {
            setVideoCanPlay(e.target);
          }}
          controls
          style={{
            width: "100%",
            height: "100%",
          }}
        ></video>
      </div>
      {/* Transcript target for plugin >= 1.1.38 via the transcriptContainer
          option. Older players ignore the option and fall back to inserting
          the transcript after the pt-16x9 box above — which now also renders
          correctly, since the player no longer stretches to the outer box. */}
      <div id={TRANSCRIPT_CONTAINER_ID}></div>
    </div>
  );
}
